import React, {
  useContext, useEffect, useState, useMemo, useRef, memo
} from 'react';
import {
  string, number
} from 'prop-types';
import classNames from 'classnames';
import {
  Button, IconButton, Input, TextField, Typography
} from '@one-thd/sui-atomic-components';
import { AddTo } from '@thd-olt-component-react/add-to';
import { Quantity } from '@thd-olt-component-react/quantity';
import { Search, TrashCan } from '@one-thd/sui-icons';
import {
  QueryProvider, useDataModel, extend,
  arrayOf,
  number as numberType,
  string as stringType,
  shape as shapeType,
  customType, params
} from '@thd-nucleus/data-sources';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import { ListRecs } from '../list-recs/ListRecs';
import { ProductListContext } from '../ProductListContext';
import {
  debounce,
  INTENT_SEARCH_EVENT,
  INTENT_EDIT_NAME_EVENT,
  cleanIntent
} from '../productListHelper';

const areEqual = (prevProps, nextProps) => {
  return (
    prevProps.groupId === nextProps.groupId
    && prevProps.intentId === nextProps.intentId
    && prevProps.intent === nextProps.intent
    && prevProps.quantity === nextProps.quantity
    && prevProps.itemOrder === nextProps.itemOrder
    && prevProps.storeId === nextProps.storeId
  );
};

export const IntentPod = memo(({
  groupId, intentId, intent, quantity, itemOrder, storeId
}) => {
  const { membershipInformation } = useStore({ varnish: true });
  const [searchKeyword, setSearchKeyword] = useState(intent);
  const [qty, setQty] = useState(quantity || 1);
  const [tempQty, setTempQty] = useState(null); // For handling temporary input state (null = not editing)
  const [typingTimeout, setTypingTimeout] = useState(null);
  const isEditingQty = useRef(false); // Track if user is actively editing quantity

  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';

  const loyaltyMembershipInput = membershipInformation?.data?.loyaltyMembership || null;

  const {
    list, resolveItem, removeIntentItem, onEditSuccess, isSharedList
  } = useContext(ProductListContext);

  const [listOperations, listOperationsResponse] = useDataModel('listOperations', {
    variables: {
      id: list?.id
    },
    context: { withAuth: true },
    fetchPolicy: 'no-cache',
    lazy: true
  });

  // Sync quantity prop changes to local state (after refetch)
  // Only sync when prop changes from external source, not from our own updates
  useEffect(() => {
    // Don't sync if user is actively editing
    if (quantity !== qty && !isEditingQty.current) {
      setQty(quantity);
      // Also clear tempQty when prop changes from external source
      setTempQty(null);
    }
  }, [quantity]); // Removed qty from dependencies to prevent loops

  // Update intent text
  const updateIntentText = async (newIntentText) => {
    if (!newIntentText || newIntentText.trim() === '' || newIntentText === intent) {
      return; // Don't update if empty or unchanged
    }

    try {
      // Handle ungrouped items - convert frontend ID back to null for backend
      // Don't know if this is a scenario we have
      const actualGroupId = groupId === '__ungrouped__' ? null : groupId;

      // Simple mutation call - only update this single intent item
      await listOperations({
        variables: {
          operationName: 'UpdateItems',
          id: list.id,
          groups: [{
            groupId: actualGroupId,
            products: [{
              intentId,
              intent: cleanIntent(newIntentText),
              quantity: qty,
              itemOrder: itemOrder || 1
            }]
          }]
        }
      });

      LIFE_CYCLE_EVENT_BUS.trigger(INTENT_EDIT_NAME_EVENT, {
        intent: {
          originalIntent: intent,
          updatedIntent: newIntentText,
          intentId
        },
        list: {
          id: list.id,
          name: list.name
        }
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error updating intent text:', error);
    }
  };

  // Update intent quantity via backend mutation
  const updateIntentQuantity = async (newQty) => {
    if (!newQty || newQty < 1) {
      return; // Don't update if invalid
    }

    try {
      // Handle ungrouped items - convert frontend ID back to null for backend
      const actualGroupId = groupId === '__ungrouped__' ? null : groupId;

      // Simple mutation call - only update this single intent item's quantity
      await listOperations({
        variables: {
          operationName: 'UpdateItems',
          id: list.id,
          groups: [{
            groupId: actualGroupId,
            products: [{
              intentId,
              intent: searchKeyword,
              quantity: newQty,
              itemOrder: itemOrder || 1
            }]
          }]
        }
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error updating intent quantity:', error);
    }
  };

  // Debounced quantity update - 1000ms delay like ListPod
  const debouncedQuantityUpdate = useMemo(() => {
    return debounce(updateIntentQuantity, 1000);
  }, []);

  // Debounced input handler - triggers update after user stops typing
  const handleIntentChange = (event) => {
    const newValue = event.target.value;
    setSearchKeyword(newValue);

    // Clear existing timeout
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }

    // Set new timeout - triggers after user stops typing for 500ms
    const newTimeout = setTimeout(() => {
      updateIntentText(newValue);
    }, 1000);

    setTypingTimeout(newTimeout);
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (typingTimeout) {
        clearTimeout(typingTimeout);
      }
      // Cancel any pending debounced quantity updates
      if (debouncedQuantityUpdate?.cancel) {
        debouncedQuantityUpdate.cancel();
      }
    };
  }, [typingTimeout, debouncedQuantityUpdate]);

  // Handle listOperations response
  useEffect(() => {
    const idFromResponse = listOperationsResponse?.data?.listOperations?.id;
    const errorFromResponse = listOperationsResponse?.error;

    if (!listOperationsResponse.loading && (idFromResponse || errorFromResponse)) {
      if (idFromResponse) {
        // Don't refetch immediately - this can override user input
        // Just call onEditSuccess to notify parent
        onEditSuccess({});
      } else if (errorFromResponse) {
        // eslint-disable-next-line no-console
        console.error('Error updating intent:', errorFromResponse);
      }
    }
  }, [listOperationsResponse.loading, onEditSuccess]);

  // Helper function to validate numeric input
  function isNumeric(value) {
    return /^-?\d+$/.test(value);
  }

  const onQtyUpdate = ({ quantity: newQty }) => {
    // Validate numeric input and that it's different from current
    if (!isNumeric(newQty) || newQty < 1) {
      return;
    }

    // Set editing flag to prevent prop updates during quantity change
    isEditingQty.current = true;

    // Only update if value actually changed
    if (newQty !== qty) {
      // Update local state immediately for instant UI feedback
      setQty(newQty);

      // Debounced backend update
      debouncedQuantityUpdate(newQty);

      // Clear editing flag after a short delay (longer than debounce)
      setTimeout(() => {
        isEditingQty.current = false;
      }, 1500);
    } else {
      // No change, clear flag immediately
      isEditingQty.current = false;
    }
  };

  const onDeleteItem = () => {
    if (removeIntentItem && intentId) {
      removeIntentItem({ groupId, intentId });
    }
  };

  const onIntentResolved = ({ itemId, quantity: changedQty }) => {
    resolveItem({
      groupId,
      intentId,
      itemId,
      quantity: changedQty || qty,
      itemOrder: itemOrder || 1,
      intent: searchKeyword
    });
  };

  // Unified handler for quantity input changes (mobile)
  const handleQuantityInputChange = (event) => {
    const value = event.target.value;

    // Set editing flag to prevent prop updates from overriding user input
    isEditingQty.current = true;

    // Always update tempQty to track what user is typing
    setTempQty(value);

    // If we have a valid positive number, trigger debounced update
    if (value !== '' && value !== null) {
      const parsedValue = parseInt(value, 10);
      if (!Number.isNaN(parsedValue) && parsedValue > 0) {
        // Trigger debounced update while typing
        onQtyUpdate({ quantity: parsedValue });
      }
    }
  };

  // Unified handler for quantity input blur (mobile)
  const handleQuantityInputBlur = () => {
    // When user leaves the field, validate and update
    if (tempQty === '' || tempQty === null) {
      // If empty or not set, revert to current qty
      setTempQty(null);
      isEditingQty.current = false;
      return;
    }

    const parsedValue = parseInt(tempQty, 10);

    // Only update if it's a valid positive number
    if (!Number.isNaN(parsedValue) && parsedValue > 0) {
      // Use the same onQtyUpdate function as desktop
      onQtyUpdate({ quantity: parsedValue });
      setTempQty(null); // Clear temp state after successful update
    } else {
      // Invalid value, revert to current qty
      setTempQty(null);
    }

    // Clear editing flag after blur completes
    isEditingQty.current = false;
  };

  const gridContainerClasses = classNames('sui-w-full sui-grid sui-grid-cols-12', {
    'sui-gap-6': !isMobile,
    'sui-gap-2': isMobile
  });

  const textFieldContainerClasses = classNames('sui-grid sui-pt-2px', {
    'sui-col-span-8': !isMobile,
    'sui-col-span-9': isMobile
  });

  const quantityContainerClasses = classNames('sui-pt-2', {
    'sui-col-span-4': !isMobile,
    'sui-col-span-3 sui-mr-2': isMobile
  });

  const quantityInnerGridClasses = classNames('sui-pt-2px sui-grid sui-gap-1', {
    'sui-grid-cols-3': !isMobile,
    'sui-grid-cols-1': isMobile
  });

  // For shared lists, render text-only display
  if (isSharedList) {
    return (
      <div className="sui-flex sui-w-full sui-flex-col sui-overflow-hidden">
        <div className="sui-w-full sui-grid sui-grid-cols-12 sui-gap-6">
          <div className="sui-grid sui-col-span-8 sui-pt-2px">
            <div className="sui-flex sui-items-center sui-py-2">
              <Typography variant="body-base" className="sui-text-primary">
                {searchKeyword || intent || ''}
              </Typography>
            </div>
          </div>
          <div className="sui-pt-2 sui-col-span-4">
            <div className="sui-pt-2px sui-grid sui-grid-cols-3 sui-gap-1">
              <div className="sui-col-span-2 sui-pt-1px">
                <div className="sui-flex sui-items-center sui-py-2">
                  <Typography variant="body-base" className="sui-text-primary">
                    Qty: {qty}
                  </Typography>
                </div>
              </div>
              {/* No delete button for shared lists */}
            </div>
          </div>
        </div>
        {/* No recommendations for shared lists */}
      </div>
    );
  }

  // Original editable version for non-shared lists
  return (
    <div className="sui-flex sui-w-full sui-flex-col sui-overflow-hidden">
      <div className={gridContainerClasses}>
        <div className={textFieldContainerClasses}>
          <div className="sui-flex sui-gap-4">
            <TextField
              fullWidth
              value={searchKeyword || ''}
              id="purchase-intent-text-field"
              onChange={handleIntentChange}
              InputProps={{
                inputProps: {
                  'data-testid': 'purchase-intent-input'
                }
              }}
            />
            {!isMobile && (
              <div className="sui-pt-2">
                <QueryProvider
                  cacheKey="list-add-to-materials"
                  defaultVariables={{
                    storeId,
                    isBrandPricingPolicyCompliant: true,
                    loyaltyMembershipInput,
                  }}
                >
                  <AddTo
                    addToType="List"
                    list={{
                      listId: list.id
                    }}
                    products={[]}
                    productPodItemData={{ searchKeyword, quantity: qty, onIntentResolved }}
                    CustomAddToActionElement={({ onClick }) => (
                      <Button
                        aria-label="intent-pod-search-button"
                        data-testid="intent-pod-search-button"
                        onClick={(event) => {
                          // Section 9.1: Intent Actions Analytics
                          LIFE_CYCLE_EVENT_BUS.trigger(INTENT_SEARCH_EVENT, {
                            intentText: searchKeyword,
                            intentId,
                            groupId
                          });
                          onClick(event);
                        }}
                        variant="tertiary"
                      >
                        <Search size="small" />
                      </Button>
                    )}
                  />
                </QueryProvider>
              </div>
            )}
          </div>
        </div>
        <div className={quantityContainerClasses}>
          <div className={quantityInnerGridClasses}>
            {isMobile ? (
              <div className="sui-col-span-2">
                <Input
                  type="number"
                  value={tempQty !== null ? tempQty : qty}
                  onChange={handleQuantityInputChange}
                  onBlur={handleQuantityInputBlur}
                  fullWidth
                />
              </div>
            ) : (
              <>
                <div className="sui-col-span-2 sui-pt-1px">
                  <Quantity
                    onChange={onQtyUpdate}
                    value={qty}
                    noRightMargin
                  />
                </div>
                <div className="sui-flex sui-justify-end sui-items-center">
                  <IconButton
                    aria-label="intent-pod-delete-button"
                    data-testid="intent-pod-delete-button"
                    onClick={onDeleteItem}
                    icon={TrashCan}
                    edge="end"
                    iconSize="regular"
                    buttonSize="tight"
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <QueryProvider
        cacheKey="list-recs"
        defaultVariables={{
          storeId,
          isBrandPricingPolicyCompliant: true,
          loyaltyMembershipInput
        }}
      >
        <ListRecs
          quantity={qty}
          keyword={searchKeyword}
          onIntentResolved={onIntentResolved}
        />
      </QueryProvider>
    </div>
  );
}, areEqual);

IntentPod.displayName = 'IntentPod';

IntentPod.dataModel = extend(
  {
    listOperations: params({
      operationName: customType('Operation').enum(
        ['Delete', 'RemoveItems', 'UpdateItems', 'Create', 'Update', 'AddItems'], 'Create'),
      id: stringType().isRequired(),
      groups: arrayOf(customType('GroupInput').shape({
        groupId: stringType(),
        groupName: stringType(),
        groupOrder: stringType(),
        products: arrayOf(customType('ProductInput').shape({
          itemId: stringType(),
          quantity: numberType(),
          itemOrder: numberType(),
          comments: stringType(),
          intent: stringType()
        }))
      }))
    }).mutation().shape({
      id: stringType(),
      groups: arrayOf(shapeType({
        groupId: stringType(),
        status: stringType(),
        items: arrayOf(shapeType({
          itemId: stringType(),
          quantity: numberType(),
          itemOrder: numberType(),
          comments: stringType(),
          status: stringType(),
          intent: stringType(),
          intentId: stringType()
        }))
      }))
    }),
  }
);

IntentPod.propTypes = {
  groupId: string,
  intentId: string.isRequired,
  intent: string.isRequired,
  quantity: number,
  itemOrder: number,
  storeId: string
};

IntentPod.defaultProps = {
  groupId: null,
  quantity: 1,
  itemOrder: 1,
  storeId: ''
};
